import {BasicDataTable, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {RequestParam} from "@/shared/api";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import {DashboardFilterPath} from "@/features/reports/dashboardFilterPath";
import {
	DashboardTable,
	DashboardTableBarPresentation,
	DashboardTableConfig,
	FooterDefinition,
	registerSortingFunctions
} from "@/features/reports/dashboardTable";
import {UserSettingsChanger} from "@/shared/userSettingsChanger";
import * as Constants from "@/shared/constants";
import {TimeNavigationBar} from "@/shared/ui/timeNavigationBar";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import {mergeProperties} from "@/shared/utils/misc";
import {formatDuration, getISODate} from "@/shared/utils/datefunctions";
import {DashboardUserEntry, TaskDashboardResponse, UserTaskDashboardResponse} from "@/shared/timr";
import {renderProgress} from "@/features/reports/progressBarHelper";

class TaskDashboardState {
	public static EVENT_CHANGE_TASK = "changeTask";

	private currentTaskId: number = null;

	public changeTask(taskId: number) {
		const oldTaskId = this.currentTaskId;
		this.currentTaskId = taskId;
		if (oldTaskId !== taskId) {
			$(this).triggerHandler(TaskDashboardState.EVENT_CHANGE_TASK, taskId);
		}
	}

	public getTaskId(): number {
		return this.currentTaskId;
	}

	public onTaskChanged(handler: (taskId: number) => void) {
		$(this).on(TaskDashboardState.EVENT_CHANGE_TASK, (_eventObject, args: number) => handler(args));
	}
}

export class TaskDashboardPage {
	public static COLUMN_SIZE_DURATION = "120px";
	public static COLUMN_SIZE_DURATION_LONG = "160px";
	public static COLUMN_SIZE_DURATION_BAR = "300px";

	private dashboardState: TaskDashboardState;

	private timeNavigationBar: TimeNavigationBar = null;
	private tablePresentationControl: ButtonRadioGroup<DashboardTableBarPresentation> = null;
	private filterPath: DashboardFilterPath;

	private taskDashboardTaskTable: ProjectTimeTaskTable = null;
	private taskDashboardUserTable: ProjectTimeUserTable = null;

	constructor() {
		this.dashboardState = new TaskDashboardState();
		this.dashboardState.onTaskChanged(_taskId => {
			this.taskDashboardTaskTable.filter(this.buildFilterParams());
			this.taskDashboardUserTable.filter(this.buildFilterParams());
		});

		this.timeNavigationBar = new TimeNavigationBar({
			container: $(".breadcrumb-line-dashboard"),
			timeSpanChanged: (from, to, type) => {
				BrowserStorageSettings.setReportingDateFrom(Constants.GROUP_TIME_NAV_BAR, from);
				BrowserStorageSettings.setReportingDateTo(Constants.GROUP_TIME_NAV_BAR, to);
				BrowserStorageSettings.setReportingDateType(Constants.GROUP_TIME_NAV_BAR, type);
				this.updateTaskDashboardTables();
			},
			defaultDateType: BrowserStorageSettings.getReportingDateType(Constants.GROUP_TIME_NAV_BAR),
			defaultFromDate: BrowserStorageSettings.getReportingDateFrom(Constants.GROUP_TIME_NAV_BAR),
			defaultToDate: BrowserStorageSettings.getReportingDateTo(Constants.GROUP_TIME_NAV_BAR)
		});

		this.tablePresentationControl = ButtonRadioGroup.typed({
			value: DashboardTableBarPresentation.Absolute,
			button: document.getElementById("dashboardAbsoluteButton"),
		}, {
			value: DashboardTableBarPresentation.Relative,
			button: document.getElementById("dashboardRelativeButton")
		}).onValueChanged(presentation => {
			new UserSettingsChanger("taskDashboardPresentation").changeSettingTo(DashboardTableBarPresentation[presentation]);
			this.taskDashboardTaskTable.setPresentation(presentation);
			this.taskDashboardUserTable.setPresentation(presentation);
		});

		this.filterPath = new DashboardFilterPath({
			currentFilterPathElement: $("#filterPath"),
			onFilterPathElementClick: (_e: unknown, obj: number) => this.dashboardState.changeTask(obj),
			currentFilterPathRootMessage: messages["report.rootTask"]
		});

		this.taskDashboardTaskTable = new ProjectTimeTaskTable({
			element: $("#taskDashboardTaskTable"),
			initialFilter: this.buildFilterParams(),
			detailsPopover: true,
			onRowClick: (data: DashboardUserEntry) => {
				if (this.isTableRowClickable(data)) {
					this.dashboardState.changeTask(Number(data.id));
				}
			},
			isRowClickable: (data: DashboardUserEntry) => this.isTableRowClickable(data),
			presentation: this.tablePresentationControl.getValue(),
			orderable: true,
			clampNote: false,
			allowExpand: false,
			pinRecord: (task: DashboardUserEntry) => task.id === this.dashboardState.getTaskId()
		}).onDataChanged((response: TaskDashboardResponse) => this.filterPath.updateDashboardInfo(response));

		this.taskDashboardUserTable = new ProjectTimeUserTable({
			element: $("#taskDashboardUserTable"),
			initialFilter: this.buildFilterParams(),
			detailsPopover: true,
			presentation: this.tablePresentationControl.getValue(),
			orderable: true,
			clampNote: false,
			allowExpand: false
		});

		registerSortingFunctions(() => this.dashboardState.getTaskId());
	}

	private buildFilterParams(): RequestParam[] {
		return [{
			name: "id",
			value: this.dashboardState.getTaskId()
		}, {
			name: "dateFrom",
			value: getISODate(this.timeNavigationBar.getDateFrom())
		}, {
			name: "dateTo",
			value: getISODate(this.timeNavigationBar.getDateTo())
		}];
	}

	private isTableRowClickable(data: DashboardUserEntry): boolean {
		return data && data.id && Number(data.id) !== this.dashboardState.getTaskId();
	}

	private updateTaskDashboardTables() {
		this.taskDashboardTaskTable.filter(this.buildFilterParams());
		this.taskDashboardUserTable.filter(this.buildFilterParams());
	}
}

class ProjectTimeTaskTable extends DashboardTable<DashboardUserEntry, TaskDashboardResponse> {
	private maxDuration: number;

	constructor(public config: DashboardTableConfig<DashboardUserEntry>) {
		super("/timr/reports/getTasksForTaskDashboard.json", config);

		this.onDataChanged((json: TaskDashboardResponse) => {
			const maxTaskDurations = json.tasks.results.map(task => task.duration);
			this.maxDuration = Math.max(...maxTaskDurations);
		});
	}

	protected createDataTableConfig(): DataTables.Settings {
		return mergeProperties(super.createDataTableConfig(), {
			ajax: {
				dataSrc: "tasks.results"
			}
		});
	}

	protected getColumnSettings(): TypedColumnSettings<DashboardUserEntry>[] {
		return [
			{data: "description", orderable: true, render: BasicDataTable.renderText, orderDataType: "task-dashboard-text"},
			{
				data: "duration",
				width: TaskDashboardPage.COLUMN_SIZE_DURATION,
				orderable: true,
				className: "text-right",
				render: BasicDataTable.renderDuration,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "durationBillable",
				width: TaskDashboardPage.COLUMN_SIZE_DURATION,
				orderable: true,
				className: "text-right",
				render: BasicDataTable.renderDuration,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "durationNotBillable",
				width: TaskDashboardPage.COLUMN_SIZE_DURATION_LONG,
				orderable: true,
				className: "text-right",
				render: BasicDataTable.renderDuration,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "duration",
				width: TaskDashboardPage.COLUMN_SIZE_DURATION_BAR,
				orderable: false,
				render: (data, type, recording, meta) => this.renderDurationBar(data, type, recording, meta)
			}
		];
	}

	protected createFooterDefinition(): FooterDefinition<TaskDashboardResponse> {
		return {
			1: json => formatDuration(json.tasks.total.duration),
			2: json => formatDuration(json.tasks.total.durationBillable),
			3: json => formatDuration(json.tasks.total.durationNotBillable)
		};
	}

	private renderDurationBar(_data: number, _type: string, recording: DashboardUserEntry, _meta: DataTables.CellMetaSettings) {
		const durationAnchor = this.presentation === DashboardTableBarPresentation.Relative
			? recording.duration
			: this.maxDuration;

		const progressBars = [{
			css: "progress-bar-projectTime-billable",
			progress: recording.durationBillable / durationAnchor
		}, {
			css: "progress-bar-projectTime-notBillable",
			progress: recording.durationNotBillable / durationAnchor
		}];

		return renderProgress(progressBars, {
			barScale: recording.duration / durationAnchor
		});
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected renderDetailsPopover(recording: DashboardUserEntry) {
		return super.renderDetailsPopover(recording, true);
	}
}

class ProjectTimeUserTable extends DashboardTable<DashboardUserEntry, UserTaskDashboardResponse> {
	private maxDuration: number;

	constructor(public config: DashboardTableConfig) {
		super("/timr/reports/getUsersForTaskDashboard.json", config);

		this.onDataChanged((json: UserTaskDashboardResponse) => {
			const maxUserDuration = json.users.results.map(user => user.duration);
			this.maxDuration = Math.max(...maxUserDuration);
		});
	}

	protected createDataTableConfig(): DataTables.Settings {
		return mergeProperties(super.createDataTableConfig(), {
			ajax: {
				dataSrc: "users.results"
			}
		});
	}

	protected getColumnSettings(): TypedColumnSettings<DashboardUserEntry>[] {
		return [
			{data: "description", orderable: true, render: BasicDataTable.renderText},
			{
				data: "duration",
				width: TaskDashboardPage.COLUMN_SIZE_DURATION,
				orderable: true,
				className: "text-right",
				render: BasicDataTable.renderDuration
			},
			{
				data: "durationBillable",
				width: TaskDashboardPage.COLUMN_SIZE_DURATION,
				orderable: true,
				className: "text-right",
				render: BasicDataTable.renderDuration
			},
			{
				data: "durationNotBillable",
				width: TaskDashboardPage.COLUMN_SIZE_DURATION_LONG,
				orderable: true,
				className: "text-right",
				render: BasicDataTable.renderDuration
			},
			{
				data: "duration",
				width: TaskDashboardPage.COLUMN_SIZE_DURATION_BAR,
				orderable: false,
				render: (data, type, recording, meta) => this.renderDurationBar(data, type, recording, meta)
			}
		];
	}

	protected createFooterDefinition(): FooterDefinition<UserTaskDashboardResponse> {
		return {
			1: json => formatDuration(json.users.total.duration),
			2: json => formatDuration(json.users.total.durationBillable),
			3: json => formatDuration(json.users.total.durationNotBillable)
		};
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	private renderDurationBar(_data: number, _type: string, recording: DashboardUserEntry, _meta: DataTables.CellMetaSettings) {
		const durationAnchor = this.presentation === DashboardTableBarPresentation.Relative
			? recording.duration
			: this.maxDuration;

		const progressBars = [{
			css: "progress-bar-projectTime-billable",
			progress: recording.durationBillable / durationAnchor
		}, {
			css: "progress-bar-projectTime-notBillable",
			progress: recording.durationNotBillable / durationAnchor
		}];

		return renderProgress(progressBars, {
			barScale: recording.duration / durationAnchor
		});
	}

	protected renderDetailsPopover(recording: DashboardUserEntry) {
		return super.renderDetailsPopover(recording, true);
	}
}
